import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Chip, KV } from './ModuleFrame';
import { apiGet, apiPost, apiDelete, isBackendConfigured } from '../utils/backendApi';
import { OrgAgree } from './Clickwrap';
import { legalStatus } from '../data/legal';
import { friendlyError } from '../data/entities';

// Native apps — App Builder → Surfaces. The hosted instance's SPA wrapped in
// native shells (decision native-app-bundles): two add-on bundles (Desktop =
// macOS + Windows, Mobile = iOS + Android), identity for the stores, signing
// credentials held in the plane's vault, hosted builds on GitHub Actions and
// the installers to download. Everything goes through
// /apps/:org/:instance/native (api/nativeBuildsApi.js).

const PLATFORM_LABEL = { ios: 'iOS', android: 'Android', macos: 'macOS', windows: 'Windows' };
const PLATFORM_OUT = { ios: '.ipa (TestFlight / App Store)', android: '.aab (Play) + .apk', macos: '.dmg + .app', windows: '.msi + setup.exe' };
const BUNDLE_ORDER = ['desktop', 'mobile'];
const ACTIVE = new Set(['queued', 'dispatched', 'building']);
const money = (c) => `$${Math.round((c || 0) / 100)}/mo`;
const when = (ms) => (ms ? new Date(ms).toLocaleString() : '—');
const tone = (s) => (s === 'done' ? 'ok' : s === 'failed' ? 'danger' : s === 'partial' ? 'warn' : ACTIVE.has(s) || s === 'claimed' ? 'info' : 'muted');

// The JSON sidecar each platform needs next to its binary files — rendered
// as fields so nobody has to hand-write JSON (shared/nativeVault.js layout).
const JSON_FIELDS = {
  'android.json': [['storePassword', 'Keystore password', 'password'], ['keyAlias', 'Key alias', 'text'], ['keyPassword', 'Key password', 'password']],
  'ios.json': [['p12Password', 'Certificate (.p12) password', 'password'], ['teamId', 'Apple Team ID', 'text'], ['method', 'Distribution: app-store or ad-hoc', 'text']],
  'macos.json': [['p12Password', 'Developer ID (.p12) password', 'password'], ['signingIdentity', 'Signing identity (e.g. Developer ID Application: Acme (TEAMID))', 'text'], ['apiKeyId', 'App Store Connect API key id (notarization, optional)', 'text'], ['apiIssuer', 'API issuer id (optional)', 'text']],
  'windows.json': [['password', 'Certificate (.pfx) password', 'password'], ['thumbprint', 'SHA-1 thumbprint (optional, computed when blank)', 'text']],
};

const readB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(',')[1] || '');
  r.onerror = () => reject(new Error(`could not read ${file.name}`));
  r.readAsDataURL(file);
});

export default function NativeApps({ org, app, instance, isAdmin }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [payDocs, setPayDocs] = useState(null);
  const [payKey, setPayKey] = useState('');
  const timer = useRef(null);
  const instId = instance?.id;

  const load = useCallback(async () => {
    if (!instId || !isBackendConfigured()) return;
    try { setData(await apiGet(`/apps/${org.id}/${instId}/native`)); setErr(''); } catch (ex) { setErr(friendlyError(ex)); }
  }, [org.id, instId]);
  useEffect(() => { load(); }, [load]);
  // Poll while a build is in flight.
  const active = !!data?.builds?.some((b) => ACTIVE.has(b.status));
  useEffect(() => {
    clearInterval(timer.current);
    if (active) timer.current = setInterval(load, 10e3);
    return () => clearInterval(timer.current);
  }, [active, load]);

  const run = async (key, fn) => {
    setBusy(key); setErr('');
    try { await fn(); await load(); } catch (ex) { setErr(friendlyError(ex)); } finally { setBusy(''); }
  };

  if (!instance) {
    return (
      <>
        <p className="mf-muted" style={{ margin: 0 }}>
          Native apps wrap this app's hosted instance in real iOS, Android, macOS and Windows shells — same sign-in, same data, built from here. Link or create the hosted instance first.
        </p>
        <div className="ab-next-actions" style={{ marginTop: 10 }}>
          <Link to={`/orgs/${org.id}/deployments`} className="cs-btn cs-btn-outline cs-btn-sm">Deployments</Link>
        </div>
      </>
    );
  }
  if (!isBackendConfigured()) return <p className="cs-help">The backend API is not configured in this build.</p>;
  if (!data) return err ? <p className="cs-form-error">{err}</p> : <p className="mf-muted">Loading…</p>;

  const entitled = BUNDLE_ORDER.filter((b) => data.bundles[b].entitled);
  const platforms = entitled.flatMap((b) => data.bundles[b].platforms);

  return (
    <div className="na-root">
      {payDocs && (
        <OrgAgree
          orgId={org.id}
          docs={payDocs}
          onDone={() => run(`buy-${payKey}`, async () => {
            const out = await apiPost(`/apps/${org.id}/${instId}/addons/${payKey}`, {});
            if (out?.url) window.location.assign(out.url);
          })}
        />
      )}
      {/* Bundles */}
      <div className="na-bundles">
        {BUNDLE_ORDER.map((b) => {
          const info = data.bundles[b];
          return (
            <div key={b} className={`na-bundle ${info.entitled ? 'is-on' : ''}`}>
              <div className="na-bundle-head">
                <strong>{info.label}</strong>
                {info.entitled ? <Chip tone="ok">on · {info.used}/{info.cap} builds this month</Chip> : <Chip>{money(info.feeCents)}</Chip>}
              </div>
              <p className="cs-help" style={{ margin: '4px 0 6px' }}>{info.blurb}</p>
              <p className="mf-muted" style={{ margin: 0 }}>{info.platforms.map((p) => `${PLATFORM_LABEL[p]} ${PLATFORM_OUT[p]}`).join(' · ')}</p>
              {!info.entitled && isAdmin && (
                <div className="ab-next-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={busy === `buy-${b}`} onClick={() => run(`buy-${b}`, async () => {
                    const status = await legalStatus(org.id);
                    if (!status.orgOk) { setPayDocs(status.agreements || []); setPayKey(b); return; }
                    const out = await apiPost(`/apps/${org.id}/${instId}/addons/${b}`, {});
                    if (out?.url) window.location.assign(out.url);
                  })}>{busy === `buy-${b}` ? 'Starting checkout…' : `Add ${info.label.toLowerCase()} · ${money(info.feeCents)}`}</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {entitled.length > 0 && (
        <>
          <Identity org={org} instId={instId} identity={data.identity} isAdmin={isAdmin} onSaved={load} />
          <Credentials org={org} instId={instId} platforms={platforms} creds={data.credentials} isAdmin={isAdmin} onChanged={load} />
          <BuildControls org={org} instId={instId} platforms={platforms} creds={data.credentials} ready={data.ready} busy={busy} run={run} active={active} />
          <History org={org} instId={instId} builds={data.builds} ttlDays={data.artifactTtlDays} />
        </>
      )}
      {err && <p className="cs-form-error" role="alert">{err}</p>}
    </div>
  );
}

function Identity({ org, instId, identity, isAdmin, onSaved }) {
  const [form, setForm] = useState(identity);
  const [state, setState] = useState('');
  useEffect(() => { setForm(identity); }, [identity]);
  const save = async () => {
    setState('saving');
    try { await apiPost(`/apps/${org.id}/${instId}/native/identity`, { ...form, build: Number(form.build) }); setState('saved'); onSaved(); } catch (ex) { setState(friendlyError(ex)); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <section className="na-section">
      <h3 className="na-h">Identity</h3>
      <p className="cs-help" style={{ marginTop: 0 }}>What the stores and the OS see. The bundle id cannot change after the first store submission; raise the build number on every upload.</p>
      <div className="na-grid">
        <label className="mf-muted">Bundle id<input className="cs-input" value={form.appId || ''} onChange={set('appId')} disabled={!isAdmin} placeholder="com.yourco.app" /></label>
        <label className="mf-muted">Display name<input className="cs-input" value={form.appName || ''} onChange={set('appName')} disabled={!isAdmin} /></label>
        <label className="mf-muted">URL scheme<input className="cs-input" value={form.scheme || ''} onChange={set('scheme')} disabled={!isAdmin} placeholder="myapp" /></label>
        <label className="mf-muted">Version<input className="cs-input" value={form.version || ''} onChange={set('version')} disabled={!isAdmin} placeholder="1.0.0" /></label>
        <label className="mf-muted">Build number<input className="cs-input" type="number" min="1" value={form.build || 1} onChange={set('build')} disabled={!isAdmin} /></label>
      </div>
      {isAdmin && (
        <div className="ab-next-actions" style={{ alignItems: 'center' }}>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={state === 'saving'} onClick={save}>{state === 'saving' ? 'Saving…' : 'Save identity'}</button>
          {state === 'saved' && <span className="mf-muted">Saved.</span>}
          {state && state !== 'saving' && state !== 'saved' && <span className="cs-form-error">{state}</span>}
        </div>
      )}
    </section>
  );
}

function Credentials({ org, instId, platforms, creds, isAdmin, onChanged }) {
  const [open, setOpen] = useState('');
  return (
    <section className="na-section">
      <h3 className="na-h">Signing credentials</h3>
      <p className="cs-help" style={{ marginTop: 0 }}>Stored encrypted; released to one build job at a time and wiped from the runner afterwards. Without them a platform still builds — unsigned — to prove the pipeline (Apple: a Simulator build).</p>
      <ul className="mf-list">
        {platforms.map((p) => {
          const c = creds[p];
          return (
            <li key={p} className="na-cred">
              <div className="na-cred-row">
                <strong>{PLATFORM_LABEL[p]}</strong>
                {c.onFile ? <Chip tone="ok">on file · {when(c.updatedAt)}</Chip> : <Chip>not on file</Chip>}
                {isAdmin && (
                  <span className="ab-next-actions" style={{ marginTop: 0 }}>
                    <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setOpen(open === p ? '' : p)}>{open === p ? 'Close' : c.onFile ? 'Replace' : 'Upload'}</button>
                    {c.onFile && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={async () => { if (window.confirm(`Remove the ${PLATFORM_LABEL[p]} signing files?`)) { await apiDelete(`/apps/${org.id}/${instId}/native/credentials/${p}`); onChanged(); } }}>Remove</button>}
                  </span>
                )}
              </div>
              {open === p && <CredentialForm org={org} instId={instId} platform={p} spec={c.spec} onDone={() => { setOpen(''); onChanged(); }} />}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CredentialForm({ org, instId, platform, spec, onDone }) {
  const binaries = [...spec.required, ...spec.optional].filter((n) => !n.endsWith('.json'));
  const jsonName = spec.required.find((n) => n.endsWith('.json'));
  const [files, setFiles] = useState({});
  const [fields, setFields] = useState({});
  const [state, setState] = useState('');
  const submit = async () => {
    setState('saving');
    try {
      const out = {};
      for (const n of binaries) if (files[n]) out[n] = await readB64(files[n]);
      if (jsonName) out[jsonName] = btoa(unescape(encodeURIComponent(JSON.stringify(fields))));
      await apiPost(`/apps/${org.id}/${instId}/native/credentials/${platform}`, { files: out });
      setState(''); onDone();
    } catch (ex) { setState(friendlyError(ex)); }
  };
  return (
    <div className="na-form">
      {binaries.map((n) => (
        <label key={n} className="mf-muted">{n}{spec.optional.includes(n) ? ' (optional)' : ''}
          <input className="cs-input" type="file" onChange={(e) => setFiles({ ...files, [n]: e.target.files?.[0] || null })} />
        </label>
      ))}
      {(JSON_FIELDS[jsonName] || []).map(([k, label, type]) => (
        <label key={k} className="mf-muted">{label}
          <input className="cs-input" type={type} autoComplete="off" value={fields[k] || ''} onChange={(e) => setFields({ ...fields, [k]: e.target.value })} />
        </label>
      ))}
      <div className="ab-next-actions" style={{ alignItems: 'center' }}>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={state === 'saving'} onClick={submit}>{state === 'saving' ? 'Encrypting…' : 'Store securely'}</button>
        {state && state !== 'saving' && <span className="cs-form-error">{state}</span>}
      </div>
    </div>
  );
}

function BuildControls({ org, instId, platforms, creds, ready, busy, run, active }) {
  const [picked, setPicked] = useState(() => Object.fromEntries(platforms.map((p) => [p, true])));
  useEffect(() => { setPicked((cur) => Object.fromEntries(platforms.map((p) => [p, cur[p] !== false]))); }, [platforms]);
  const chosen = platforms.filter((p) => picked[p]);
  const bundles = [...new Set(chosen.map((p) => (p === 'macos' || p === 'windows' ? 'desktop' : 'mobile')))];
  return (
    <section className="na-section">
      <h3 className="na-h">Build</h3>
      <div className="na-picks">
        {platforms.map((p) => (
          <label key={p} className="na-pick">
            <input type="checkbox" checked={!!picked[p]} onChange={(e) => setPicked({ ...picked, [p]: e.target.checked })} />
            <span>{PLATFORM_LABEL[p]}</span>
            <span className="mf-muted">{creds[p]?.onFile ? 'signed' : 'unsigned'}</span>
          </label>
        ))}
      </div>
      <div className="ab-next-actions" style={{ alignItems: 'center' }}>
        <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={!ready || !chosen.length || active || busy === 'build'} onClick={() => run('build', () => apiPost(`/apps/${org.id}/${instId}/native/builds`, { bundles, platforms: chosen }))}>
          {busy === 'build' ? 'Dispatching…' : active ? 'Build running…' : `Build ${chosen.length} platform${chosen.length === 1 ? '' : 's'}`}
        </button>
        {!ready && <span className="cs-help">The hosted instance must be live first.</span>}
        <span className="mf-muted">Runs on hosted macOS / Windows / Linux runners; 10–25 minutes.</span>
      </div>
    </section>
  );
}

function History({ org, instId, builds, ttlDays }) {
  const [err, setErr] = useState('');
  const download = async (b, p, file) => {
    try {
      const { url } = await apiGet(`/apps/${org.id}/${instId}/native/builds/${b.id}/download?platform=${p}&file=${encodeURIComponent(file)}`);
      window.location.assign(url);
    } catch (ex) { setErr(friendlyError(ex)); }
  };
  if (!builds?.length) return <p className="mf-muted">No builds yet.</p>;
  return (
    <section className="na-section">
      <h3 className="na-h">Builds</h3>
      <ul className="mf-list">
        {builds.map((b) => (
          <li key={b.id} className="na-build">
            <div className="na-cred-row">
              <Chip tone={tone(b.status)}>{b.status}</Chip>
              <span className="mf-muted">{when(b.createdAt)} · {b.identity?.appName} {b.identity?.version} ({b.identity?.build})</span>
              {b.message && !['done'].includes(b.status) && <span className="mf-muted">· {b.message}</span>}
            </div>
            <KV rows={(b.requested || []).map((p) => {
              const s = b.platforms?.[p] || {};
              return [PLATFORM_LABEL[p], (
                <span className="na-plat">
                  <Chip tone={tone(s.status)}>{s.status || 'queued'}</Chip>
                  {b.signed?.[p] ? <span className="mf-muted">signed</span> : <span className="mf-muted">unsigned</span>}
                  {(s.artifacts || []).map((a) => (
                    <button key={a.file} type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => download(b, p, a.file)}>{a.file} · {(a.bytes / 1e6).toFixed(1)} MB</button>
                  ))}
                  {s.status === 'failed' && s.message && <span className="cs-form-error">{s.message}</span>}
                  {s.runUrl && <a href={s.runUrl} target="_blank" rel="noreferrer" className="mf-muted">log ↗</a>}
                </span>
              )];
            })} />
          </li>
        ))}
      </ul>
      <p className="cs-help">Installers stay downloadable for {ttlDays} days.</p>
      {err && <p className="cs-form-error">{err}</p>}
    </section>
  );
}
